import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'

import { QUERY_CONFIG, queryKeys } from '../../lib/react-query'
// Imported from the modules, not the `services/api` barrel: the barrel pulls in
// `componentIcons`, whose `require.context` is fatal under `react-scripts test`.
import { issuesApi } from '../../services/api/issues.api'
import { vulnerabilitiesApi } from '../../services/api/vulnerabilities.api'
import { useAuthStore } from '../../store/authStore'
import { useFeature } from '../../Utils/featureFlagEngine'
import type {
  BulkWrite,
  IssueDetail,
  IssueListParams,
  IssueListResponse,
  IssuePriority,
  IssueRow,
  IssueTimelineResponse,
  PriorityWrite,
  StateWrite,
} from '../../app/modules/types/issues'
import type { OccurrencesResponse } from '../../app/modules/types/vulnerabilities'

/**
 * React Query hooks for the Issues page (issue = finding, `/vulnerabilities/*`).
 *
 * Two decisions run through the whole file:
 *
 * 1. **Every single-issue write seeds the detail cache from its own response.**
 *    Each action re-reads the finding through the same annotated query, so the
 *    body already carries the recomputed `priority`, `sla` and due date.
 * 2. **Writes PATCH the loaded rows, they never refetch pages.** Lists are
 *    page-numbered: a page-number refetch after a write skips rows, because
 *    the rows have moved between pages. So a write replaces the row in every
 *    cached list in place (a single-state list gets the card moved, with both
 *    counts adjusted) and only cheap single-key reads are invalidated. A bulk
 *    action drops the loaded pages and restarts from page 1 instead.
 */

/** Serialize the wire params exactly as they travel — the cache-key form. */
export function qsOf(params: IssueListParams): string {
  const usp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    usp.set(key, String(value))
  }
  return usp.toString()
}

/** The `state` set a cached list was asked for, read back from its key; null = any state. */
function statesOfKey(key: readonly unknown[]): string[] | null {
  const qs = typeof key[3] === 'string' ? key[3] : ''
  const value = new URLSearchParams(qs).get('state')
  return value ? value.split(',') : null
}

type Pages = InfiniteData<IssueListResponse, number>

/**
 * Patch one issue row through every cached list.
 *
 * The ONE row-level cache writer for the issues space (optimistic move,
 * post-write reconciliation, detail-panel writes). A list keeps the row when
 * it still matches the list's `state` filter, drops it (count - 1) when it no
 * longer does, and a single-state list that the row now matches receives it
 * (count + 1) when the row is known from another cached list.
 */
export function patchIssueRowIntoCaches(
  queryClient: QueryClient,
  orgId: string,
  patch: Partial<IssueRow> & { id: string }
) {
  let known: IssueRow | undefined
  for (const [, data] of queryClient.getQueriesData<Pages>({
    queryKey: queryKeys.issues.lists(orgId),
  })) {
    for (const p of data?.pages ?? []) {
      const hit = p.results.find((r) => r.id === patch.id)
      if (hit) {
        known = hit
        break
      }
    }
    if (known) break
  }
  const merged = known ? ({ ...known, ...patch } as IssueRow) : undefined

  for (const [key, data] of queryClient.getQueriesData<Pages>({
    queryKey: queryKeys.issues.lists(orgId),
  })) {
    if (!data) continue
    const states = statesOfKey(key)
    const has = data.pages.some((p) => p.results.some((r) => r.id === patch.id))
    const matches = !states || !merged || states.includes(merged.state)
    let next: Pages = data
    if (has && matches) {
      next = {
        ...data,
        pages: data.pages.map((p) => ({
          ...p,
          results: p.results.map((r) => (r.id === patch.id ? { ...r, ...patch } : r)),
        })),
      }
    } else if (has && !matches) {
      next = {
        ...data,
        pages: data.pages.map((p, i) => ({
          ...p,
          results: p.results.filter((r) => r.id !== patch.id),
          total_items: i === 0 ? Math.max(0, p.total_items - 1) : p.total_items,
        })),
      }
    } else if (!has && merged && states && states.length === 1 && states[0] === merged.state) {
      // A single-state column receiving the card.
      next = {
        ...data,
        pages: data.pages.map((p, i) =>
          i === 0 ? { ...p, results: [merged, ...p.results], total_items: p.total_items + 1 } : p
        ),
      }
    }
    if (next !== data) queryClient.setQueryData(key, next)
  }
}

/**
 * Every read is gated on BOTH the organization and the `issues` feature.
 *
 * `FeatureGuard` only hides UI: a hook left ungated keeps firing requests for
 * a feature the user cannot see — and against an endpoint that denies by
 * default that is a burst of 403s on every page load.
 */
function useIssuesGate() {
  const orgId = useAuthStore((state) => state.orgId)
  const { isEnabled } = useFeature()
  return { orgId, enabled: !!orgId && isEnabled('issues') }
}

/* -------------------------------------------------------------------- reads */

export function useIssues(params: IssueListParams, enabled = true) {
  const { orgId, enabled: gate } = useIssuesGate()
  return useInfiniteQuery({
    queryKey: queryKeys.issues.list(orgId, qsOf(params)),
    queryFn: ({ pageParam }) => issuesApi.getAll({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last: IssueListResponse) =>
      last.current_page < last.total_pages ? last.current_page + 1 : undefined,
    enabled: gate && enabled,
  })
}

/** The audit trail, newest first, page-numbered. */
export function useIssueTimeline(id: string | null, enabled = true) {
  const { orgId, enabled: gate } = useIssuesGate()
  return useInfiniteQuery({
    queryKey: queryKeys.issues.activity(orgId, id ?? ''),
    queryFn: ({ pageParam }) => issuesApi.getTimeline(id as string, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last: IssueTimelineResponse) =>
      last.current_page < last.total_pages ? last.current_page + 1 : undefined,
    enabled: gate && !!id && enabled,
    select: (data) => ({ events: data.pages.flatMap((p) => p.results ?? []) }),
  })
}

/** The scan sightings behind an issue (its finding's occurrences), page-numbered. */
export function useIssueOccurrences(id: string | null, enabled = true) {
  const { orgId, enabled: gate } = useIssuesGate()
  return useInfiniteQuery({
    queryKey: queryKeys.issues.occurrences(orgId, id ?? ''),
    queryFn: ({ pageParam }) =>
      vulnerabilitiesApi.getOccurrences(id as string, { page: pageParam, pageSize: 10 }),
    initialPageParam: 1,
    getNextPageParam: (last: OccurrencesResponse) =>
      last.current_page < last.total_pages ? last.current_page + 1 : undefined,
    enabled: gate && !!id && enabled,
    select: (data) => ({ occurrences: data.pages.flatMap((p) => p.results ?? []) }),
  })
}

/** Facet values present in this organization, with counts. */
export function useIssueFacets() {
  const { orgId, enabled: gate } = useIssuesGate()
  return useQuery({
    queryKey: queryKeys.issues.facets(orgId),
    queryFn: () => issuesApi.getFacets(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: gate,
  })
}

/**
 * The vocabulary and the priority explainer.
 *
 * Cached long: it is deployment configuration, not data. `retry: false`
 * because the page works without it — the explainer collapses and the state
 * picker falls back to the contract's own terminal set.
 */
export function useIssueConfig() {
  const { orgId, enabled: gate } = useIssuesGate()
  return useQuery({
    queryKey: queryKeys.issues.config(orgId),
    queryFn: () => issuesApi.getConfig(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    retry: false,
    enabled: gate,
  })
}

export function useIssue(id: string | null) {
  const { orgId, enabled: gate } = useIssuesGate()
  return useQuery({
    queryKey: queryKeys.issues.detail(orgId, id ?? ''),
    queryFn: () => issuesApi.getById(id as string),
    enabled: gate && !!id,
  })
}

/* ---------------------------------------------------------------- mutations */

/**
 * The write response IS the fresh detail: seed the detail cache with it and
 * reconcile the row caches through the one patcher. Only cheap single-key
 * reads (facets, the timeline) are invalidated; the lists are PATCHED, never
 * refetched. The vulnerabilities page and detail share these findings, so
 * their caches are invalidated too and stay in step.
 */
function useIssueWriteSync() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  return (detail: IssueDetail) => {
    queryClient.setQueryData(queryKeys.issues.detail(orgId, detail.id), detail)
    patchIssueRowIntoCaches(queryClient, orgId, detail)
    queryClient.invalidateQueries({ queryKey: queryKeys.issues.facets(orgId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.issues.activity(orgId, detail.id) })
    queryClient.invalidateQueries({ queryKey: queryKeys.vulnerabilities.all(orgId) })
  }
}

/**
 * Drop every loaded page of every list and load page 1 again. The bulk route
 * can move up to 10,000 issues at once (ids or a filter); patching that many rows in place would
 * be its own sort of lie, and a page-number refetch skips rows.
 */
function useIssueListReset() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()
  return () => {
    void queryClient.resetQueries({ queryKey: queryKeys.issues.lists(orgId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.issues.facets(orgId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.vulnerabilities.all(orgId) })
  }
}

/**
 * Move an issue between states — the board drag AND the detail panel's
 * State card.
 *
 * OPTIMISTIC: `onMutate` cancels any in-flight list refetch (it would
 * otherwise resolve after the patch and paint the pre-drag state back on
 * screen), snapshots the lists prefix, and applies the projected row
 * (`{ id, state }`) — the card is in its new column before the request
 * leaves. `onSuccess` reconciles with the authoritative response (recomputed
 * priority/sla land without a flicker, and loaded pages stay loaded). Any
 * failure restores the exact snapshot; the visible feedback is the caller's
 * — a toast on the board, the inline banner in the panel.
 */
export function useSetIssueState() {
  const sync = useIssueWriteSync()
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, write }: { id: string; write: StateWrite }) =>
      issuesApi.setState(id, write),
    onMutate: async ({ id, write }) => {
      if (!orgId) return
      await queryClient.cancelQueries({ queryKey: queryKeys.issues.lists(orgId) })
      const prevLists = queryClient.getQueriesData<Pages>({
        queryKey: queryKeys.issues.lists(orgId),
      })
      patchIssueRowIntoCaches(queryClient, orgId, { id, state: write.state })
      return { prevLists }
    },
    onError: (_error, _variables, context) => {
      if (!orgId || !context) return
      for (const [key, data] of context.prevLists) queryClient.setQueryData(key, data)
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.facets(orgId) })
    },
    onSuccess: (detail) => {
      sync(detail)
      // The patch above moves the card; refetching the lists as well guarantees
      // every column (and the table) agrees with the server after the write.
      if (orgId) queryClient.invalidateQueries({ queryKey: queryKeys.issues.lists(orgId) })
    },
  })
}

export function useAssignIssue() {
  const sync = useIssueWriteSync()
  return useMutation({
    mutationFn: ({ id, assignee }: { id: string; assignee: string | null }) =>
      issuesApi.assign(id, assignee),
    onSuccess: sync,
  })
}

export function useSetIssuePriority() {
  const sync = useIssueWriteSync()
  return useMutation({
    mutationFn: ({ id, write }: { id: string; write: PriorityWrite }) =>
      issuesApi.setPriority(id, write),
    onSuccess: sync,
  })
}

export function usePutBack() {
  const sync = useIssueWriteSync()
  return useMutation({
    mutationFn: ({ id }: { id: string }) => issuesApi.putBack(id),
    onSuccess: sync,
  })
}

export function useMarkDuplicate() {
  const sync = useIssueWriteSync()
  return useMutation({
    mutationFn: ({
      id,
      duplicate_of,
      justification,
    }: {
      id: string
      duplicate_of: string
      justification: string
    }) => issuesApi.markDuplicate(id, { duplicate_of, justification }),
    onSuccess: sync,
  })
}

/**
 * One action over the selected ids, or over every issue a filter matches
 * (up to 10,000 on a real run).
 *
 * It answers 200 even when every id failed, so the caller reads the outcomes
 * out of the body. A dry run changes nothing; a real run drops the loaded
 * pages and restarts from page 1.
 */
export function useBulkIssues() {
  const resetLists = useIssueListReset()
  return useMutation({
    mutationFn: (body: BulkWrite) => issuesApi.bulk(body),
    onSuccess: (_data, body) => {
      if (!body.dry_run) resetLists()
    },
  })
}

/* -------------------------------------------------------------- saved views */

export function useSavedViews() {
  const { orgId, enabled: gate } = useIssuesGate()
  return useQuery({
    queryKey: queryKeys.issues.views(orgId),
    queryFn: () => issuesApi.getViews(),
    enabled: gate,
  })
}

function useViewsSync() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.issues.views(orgId) })
}

export function useCreateSavedView() {
  const sync = useViewsSync()
  return useMutation({
    mutationFn: ({ name, query }: { name: string; query: string }) =>
      issuesApi.createView(name, query),
    onSuccess: sync,
  })
}

export function usePatchSavedView() {
  const sync = useViewsSync()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name?: string; query?: string } }) =>
      issuesApi.patchView(id, body),
    onSuccess: sync,
  })
}

export function useDeleteSavedView() {
  const sync = useViewsSync()
  return useMutation({
    mutationFn: ({ id }: { id: string }) => issuesApi.deleteView(id),
    onSuccess: sync,
  })
}

/** Sharing needs `api:issue_admin`; a 403 here is a real runtime outcome. */
export function useShareSavedView() {
  const sync = useViewsSync()
  return useMutation({
    mutationFn: ({ id, shared }: { id: string; shared: boolean }) =>
      issuesApi.shareView(id, shared),
    onSuccess: sync,
  })
}

/* ------------------------------------------------------------- sla policy */

/** `retry: false`: a 403 is an ANSWER, retrying only delays the "no access" panel. */
export function useSlaPolicy(enabled = true) {
  const { orgId, enabled: gate } = useIssuesGate()
  return useQuery({
    queryKey: queryKeys.issues.slaPolicy(orgId),
    queryFn: () => issuesApi.getSlaPolicy(),
    enabled: gate && enabled,
    retry: false,
  })
}

export function usePatchSlaPolicy() {
  const orgId = useAuthStore((state) => state.orgId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ priority, days }: { priority: IssuePriority; days: number }) =>
      issuesApi.patchSlaPolicy(priority, days),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.slaPolicy(orgId) }),
  })
}
